import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  ShieldPlus,
  ListChecks,
  History,
  ScrollText,
  Radar,
  LogOut,
  UploadCloud,
  Users as UsersIcon,
  Search as SearchIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ROLE_LABEL = {
  admin: "Administrator",
  analyst: "AML Analyst",
  reviewer: "Compliance Lead",
  auditor: "Read-Only Auditor",
};

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/search", label: "Entity Search", icon: SearchIcon, testid: "nav-search", roles: ["analyst", "reviewer", "admin"] },
  { to: "/new", label: "New Screening", icon: ShieldPlus, testid: "nav-new-screening", roles: ["analyst", "reviewer", "admin"] },
  { to: "/batch", label: "Batch Screening", icon: UploadCloud, testid: "nav-batch", roles: ["analyst", "reviewer", "admin"] },
  { to: "/review", label: "Review Queue", icon: ListChecks, testid: "nav-review-queue" },
  { to: "/cases", label: "Case History", icon: History, testid: "nav-case-history" },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-audit-log" },
  { to: "/sanctions", label: "Sanctions Feeds", icon: Radar, testid: "nav-sanctions" },
  { to: "/users", label: "Team & Access", icon: UsersIcon, testid: "nav-users", roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  return (
    <div className="min-h-screen grid-bg" data-testid="app-shell">
      <header className="sticky top-0 z-50 h-16 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl">
        <div className="flex h-full items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white p-1 ring-1 ring-slate-200">
              <img src="/logo.png" alt="TheRiskFree" className="h-9 w-9 object-contain" data-testid="navbar-logo" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-sm font-extrabold tracking-tight text-slate-100">
                TheRiskFree
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                AML / KYC Risk Screening
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-slate-200" data-testid="current-user-name">
                {user?.name}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-blue-400" data-testid="role-selector">
                {ROLE_LABEL[user?.role] || user?.role}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-slate-600 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1500px] gap-0">
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-60 shrink-0 border-r border-slate-800 p-4 lg:block">
          <nav className="flex flex-col gap-1">
            {visibleNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-blue-600/15 text-blue-300 ring-1 ring-blue-500/30"
                      : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
                  )
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900/50 p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
              Engine
            </div>
            <div className="mt-1 text-xs text-slate-400">
              Deterministic Python scoring · GPT enrichment (schema-validated) · fail-safe defaults.
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
